import { useApiClient } from "@positive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { Toast } from "@ui5/webcomponents-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePreferences } from "../PreferencesContext";
import { useIdentity } from "./IdentityProvider";
import { saveAccountPreferences, sessionQueryKey, withAccountChoices, type SessionContext } from "./session";

/**
 * Language, digit shape and Hijri display live on the user's account (decision I6, contract §5a): once the session is
 * loaded the account's values replace this browser's, and later changes (globe in the Shell Bar, My profile) are saved
 * with `PUT /api/platform/me/preferences`. A failed save reverts the change and says so quietly. Not connected in the
 * operator console or in a support session (the operator's language is not the user's choice).
 */
export function AccountPreferences() {
  const { t } = useTranslation("shell");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { session, mode } = useIdentity();
  const { connectAccount, disconnectAccount } = usePreferences();
  const [failed, setFailed] = useState(false);
  const latest = useRef(session);
  useEffect(() => {
    latest.current = session;
  });
  const userId = mode.kind === "tenant" && session && !session.impersonation ? session.user.id : null;

  useEffect(() => {
    const signedIn = latest.current;
    if (!userId || !signedIn) return undefined;
    const { preferences } = signedIn;
    connectAccount(
      { language: preferences.language, digits: preferences.digits, hijri: preferences.hijriDisplay },
      signedIn.locale.language,
      {
        save: async (choices) => {
          // The theme stays per browser: the stored value goes back unchanged.
          const theme = latest.current?.preferences.theme ?? null;
          await saveAccountPreferences(client, {
            language: choices.language,
            theme,
            digits: choices.digits,
            hijriDisplay: choices.hijri,
          });
          queryClient.setQueryData<SessionContext>(sessionQueryKey, (cached) =>
            cached ? withAccountChoices(cached, choices) : cached,
          );
        },
        onFailure: () => {
          setFailed(true);
        },
      },
    );
    return disconnectAccount;
  }, [client, connectAccount, disconnectAccount, queryClient, userId]);

  return (
    <Toast
      open={failed}
      data-testid="preferences-save-failed"
      onClose={() => {
        setFailed(false);
      }}
    >
      {t("preferences.saveFailed")}
    </Toast>
  );
}
